/**
 * The authorities a client reports to, with their Hebrew labels.
 *
 * Centralised for the same reason the status vocabulary is: the matrix header, the
 * client card, the reminder variables and any export must all name an authority
 * identically. A column reading "מע״מ" in one screen and "מס ערך מוסף" in another
 * looks like two different things to the person using it.
 */
export type AuthorityKey =
  "vat" | "vat_detailed" | "income_tax_advance" | "withholding_tax" | "national_insurance";

export interface AuthorityMeta {
  /** Full name, for prose and for reminder text. */
  label: string;
  /** Abbreviated, for the matrix column headers where space is tight. */
  short: string;
  /** Only owed by clients that employ workers. */
  requiresEmployer: boolean;
}

export const AUTHORITIES: Record<AuthorityKey, AuthorityMeta> = {
  vat: { label: "מע״מ", short: "מע״מ", requiresEmployer: false },
  vat_detailed: { label: "מע״מ מפורט (PCN874)", short: "מפורט", requiresEmployer: false },
  income_tax_advance: { label: "מקדמות מס הכנסה", short: "מקדמות", requiresEmployer: false },
  withholding_tax: { label: "ניכויים ממשכורת", short: "ניכויים", requiresEmployer: true },
  national_insurance: { label: "ביטוח לאומי", short: "ב״ל", requiresEmployer: true },
};

/**
 * Display order for the matrix columns, matching the hero mockup's reporting board.
 *
 * Detailed VAT is deliberately absent: it is a variant of the VAT filing rather
 * than a separate obligation, and giving it a column would imply a client owes
 * both.
 */
export const MATRIX_AUTHORITIES: readonly AuthorityKey[] = [
  "vat",
  "income_tax_advance",
  "withholding_tax",
  "national_insurance",
] as const;

export function authorityLabel(key: AuthorityKey): string {
  return AUTHORITIES[key].label;
}
